import React from "react";
import { useNavigate } from "react-router-dom";
import smImage from "../../assets/images/about-sm-screen.png";
import ssbImage from "../../assets/images/about-ssb-screen.png";
import "./Home.css";
import { useSalon } from "../../context/SalonContext";
import "../../style.css";
import { FaStore, FaCut, FaCalendarCheck, FaBell, FaCheckCircle, FaClock, FaTag, FaUserCheck } from "react-icons/fa";

/**
 * Sub-component for App Info Cards
 */
const AppInfoCard = ({ title, description, image, link, buttonText }) => (
    <div className="col-12 col-md-6 col-lg-5 d-flex justify-content-center">
        <div className="card app-info-card shadow border-0 h-100 text-center transition-hover w-100">
            <div className="p-4 d-flex justify-content-center">
                <img
                    src={image}
                    className="img-fluid rounded app-image mb-3 d-block mx-auto"
                    alt={title}
                />
            </div>
            <div className="card-body d-flex flex-column pt-0 align-items-center">
                <h5 className="card-title fw-bold text-primary">{title}</h5>
                <p className="card-text text-muted mb-4">
                    {description}
                </p>
                <a
                    href={link}
                    className="btn btn-primary mt-auto px-5 w-100"
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {buttonText}
                </a>
            </div>
        </div>
    </div>
);

/**
 * Sub-component for Step Cards - Mobile Inspired
 */
const StepItem = ({ icon: Icon, title, description }) => (
    <div className="col-6 col-md-3 d-flex flex-column align-items-center text-center mb-4">
        <div className="step-icon-wrapper">
            <div className="step-icon-inner">
                <Icon className="step-icon" />
            </div>
        </div>
        <h6 className="step-title mt-3">{title}</h6>
        <p className="step-desc small text-muted d-none d-md-block">{description}</p>
    </div>
);

/**
 * Sub-component for Benefit Cards
 */
const BenefitItem = ({ icon: Icon, title, description }) => (
    <div className="col-6 col-md-3 d-flex justify-content-center">
        <div className="card benefit-item-card p-3 border-0 shadow-sm h-100 text-center bg-white rounded-3 w-100">
            <div className="benefit-icon-box mb-2">
                <Icon />
            </div>
            <h6 className="fw-bold mb-1">{title}</h6>
            <p className="small text-muted mb-0">{description}</p>
        </div>
    </div>
);

export default function Home() {
    const navigate = useNavigate();
    const { accessToken, salonUuid, salonName } = useSalon();

    const handleBookingClick = () => {
        if (accessToken) {
            navigate("/booking");
        } else {
            navigate("/login?redirect=/booking");
        }
    };

    return (
        <div className="home-wrapper">
            {/* Hero Section - Mobile Splash Style */}
            <header className="hero-header text-center py-5">
                <div className="overlay-dark"></div>
                <div className="content-wrapper position-relative z-index-2">
                    <div className="diamond-accent">♦</div>
                    <h1 className="hero-main-title display-4 fw-bold mb-3">
                        Beauty Trends
                    </h1>
                    <p className="hero-tagline lead mb-4 fs-4">
                        Book Your Style. Your Time. Your Way.
                    </p>

                    {salonUuid && (
                        <button
                            onClick={handleBookingClick}
                            className="btn btn-premium btn-lg px-5 py-3 shadow-lg fw-bold rounded-pill"
                        >
                            {salonName ? `Book at ${salonName}` : "Book an Appointment Now"}
                        </button>
                    )}
                </div>
            </header>

            {/* How It Works - Mobile Icon Style */}
            <section className="how-it-works-mobile py-5">
                <div className="content-wrapper">
                    <div className="row g-2 justify-content-center">
                        <StepItem
                            icon={FaStore}
                            title="Choose Salon"
                            description="Find and select your preferred salon."
                        />
                        <StepItem
                            icon={FaCut}
                            title="Select Services"
                            description="Pick from a wide range of services."
                        />
                        <StepItem
                            icon={FaCalendarCheck}
                            title="Select Slot"
                            description="Choose a date and time that fits."
                        />
                        <StepItem
                            icon={FaBell}
                            title="Reminders"
                            description="Get notified about your visit."
                        />
                    </div>
                </div>
            </section>

            {/* Apps Introduction */}
            <section className="py-5 bg-white-soft">
                <div className="content-wrapper">
                    <div className="row g-4 justify-content-center">
                        <AppInfoCard
                            title="Salon Management App"
                            description="For salon owners to manage services, staff, and appointments with ease."
                            image={smImage}
                            link="https://salonmanagement.in/"
                            buttonText="Learn More"
                        />
                        <AppInfoCard
                            title="Beauty Trends App"
                            description="For customers to browse salons, choose services, and book appointments instantly."
                            image={ssbImage}
                            link="https://salonmanagement.in/"
                            buttonText="Get Started"
                        />
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-5 mb-4">
                <div className="content-wrapper text-center">
                    <h2 className="fw-bold mb-5 section-heading">Why Choose Us?</h2>
                    <div className="row g-4 justify-content-center">
                        <BenefitItem
                            icon={FaUserCheck}
                            title="Hassle-Free"
                            description="Book anytime, anywhere."
                        />
                        <BenefitItem
                            icon={FaClock}
                            title="Real-Time"
                            description="Live availability."
                        />
                        <BenefitItem
                            icon={FaTag}
                            title="Transparent"
                            description="Upfront pricing."
                        />
                        <BenefitItem
                            icon={FaCheckCircle}
                            title="No Waiting"
                            description="Scheduled service."
                        />
                    </div>
                </div>
            </section>
        </div>
    );
}
